import type { Observable } from 'rxjs';
import { EMPTY, filter, from, map } from 'rxjs';

import type {
  TransportAvailabilityCriteria,
  TransportListResponse,
  HotelBookableOptionWithRoomsAndDateAndPrices,
  ActivityAvailabilityCriteria,
  AccommodationAvailabilityCriteria,
  GraphQL,
  ActivityAvailabilityResponse,
} from '@libs/trip-planner-interfaces';
import { assertValue, isDefined, logged, normalizeDate } from '@libs/utils';

import { getApolloClient } from './infrastructure';
import {
  GET_TRANSPORT_AVAILABILITY_QUERY,
  SEARCH_PRODUCT_AVAILABILITY_SUBSCRIPTION,
  SEARCH_TICKET_RATES_QUERY,
} from './queries';
import { getTicketRequestQL } from './utils/requestMappers/ticketRequestQL';
import { toActivityAvailabilityResponse } from './utils/responseMappers/ticketResponseQL';
import { getCheapestHotelStayRequestQL } from './utils/requestMappers/cheapestHotelStayRequestQL';
import { toProductAvailabilityResponseQL } from './utils/responseMappers/productAvailabilityResponseQL';
import { getTransportListRequestQL } from './utils/requestMappers/transportListRequestQL';
import { toTransportAvailabilityResponse } from './utils/responseMappers/transportAvailabilityResponseQL';

export class ProductAvailabilitySearchService {
  public searchAccommodationProductAvailability(
    criteria: AccommodationAvailabilityCriteria
  ): Observable<HotelBookableOptionWithRoomsAndDateAndPrices> {
    const request = getCheapestHotelStayRequestQL(criteria);

    const productAvailabilityResponse = getApolloClient().subscribe<
      GraphQL.searchProductAvailabilitySubscription,
      GraphQL.searchProductAvailabilitySubscriptionVariables
    >({
      variables: {
        request,
      },
      query: SEARCH_PRODUCT_AVAILABILITY_SUBSCRIPTION,
    });

    return productAvailabilityResponse.pipe(
      logged('Product Availability Response'),
      map((fetchResult) => fetchResult.data),
      filter(isDefined),
      map(toProductAvailabilityResponseQL)
    );
  }

  public searchActivityProductAvailability(
    criteria: ActivityAvailabilityCriteria
  ): Observable<ActivityAvailabilityResponse> {
    const request = getTicketRequestQL(criteria);

    const response = getApolloClient().query<
      GraphQL.searchTicketAvailabilityQuery,
      GraphQL.searchTicketAvailabilityQueryVariables
    >({
      query: SEARCH_TICKET_RATES_QUERY,
      variables: {
        request,
      },
      fetchPolicy: 'no-cache',
    });

    return from(response).pipe(
      map((queryResult) => {
        assertValue(
          queryResult.data,
          'activitySuggestionService > getSuggestions > response data not available'
        );

        return toActivityAvailabilityResponse(queryResult.data);
      })
    );
  }

  public searchTransportProductAvailability(
    criteria: TransportAvailabilityCriteria
  ): Observable<TransportListResponse> {
    const ingredientsList = criteria.userCriteria.recipe.ingredients;

    // FYI: This is to shift date by timezone offset
    criteria.userCriteria.day = normalizeDate(criteria.userCriteria.day);

    if (
      ingredientsList === null ||
      ingredientsList.length === 0 ||
      ingredientsList[criteria.userCriteria.ingredientIndex] === undefined
    ) {
      return EMPTY;
    }

    const request = getTransportListRequestQL(criteria);

    const response = getApolloClient().query<
      GraphQL.getTransportAvailabilityQuery,
      GraphQL.getTransportAvailabilityQueryVariables
    >({
      query: GET_TRANSPORT_AVAILABILITY_QUERY,
      variables: {
        request,
      },
    });

    const ingredientAdapter =
      ingredientsList[criteria.userCriteria.ingredientIndex];

    assertValue(
      ingredientAdapter,
      'search-service-smart-search > productAvailabilitySearchService.ts > searchTransportProductAvailability > ingredient'
    );

    return from(response).pipe(
      map((queryResult) => {
        assertValue(
          queryResult.data,
          'activitySuggestionService > getSuggestions > response data not available'
        );

        return toTransportAvailabilityResponse(
          queryResult.data,
          ingredientAdapter
        );
      })
    );
  }
}
